import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { NotificationsActive, NotificationsOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAuth } from "../../features/auth/authSlice";
import { Role } from "../../features/auth/types/authType";
import { RoleTypes } from "../../features/auth/types/roleTypes";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import {
  fetchNotifications,
  markAllAsReadNotifications,
  markAsReadNotification,
  removeNotification,
  selectProcesses,
} from "../../features/process/processSlices";
import {
  Notification,
  NotificationAudience,
  NotificationStatus,
} from "../../features/process/types/processTypes";
import Paging from "../Layout/Paging";
import { NotificationTable } from "../../features/notifications/NotificationTable";
import { useNotificationSetup } from "../../features/notifications/useNotificationSetup";
import {
  getUserNotificationAudiences,
  hasNotificationAudience,
} from "../../features/notifications/notificationAudiences";

function Notifications() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const {
    notificationsWithCount: { notifications, totalCount },
    loading,
  } = useAppSelector(selectProcesses);

  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [statusTab, setStatusTab] = useState<"new" | "read" | "all">("new");
  const [audienceTab, setAudienceTab] = useState<"all" | NotificationAudience>(
    "all",
  );
  const [pushSnackbar, setPushSnackbar] = useState(false);

  const {
    pushPermission,
    pushSubscribed,
    pushSupported,
    enablePush,
    disablePush,
    shouldShowPushPrompt,
    dismissPushPrompt,
  } = useNotificationSetup();

  const roles = (user?.roles ?? []) as Role[];
  const userAudiences = getUserNotificationAudiences(roles);
  const showPipelineTab = hasNotificationAudience(
    roles,
    NotificationAudience.pipeline,
  );
  const showRecordsTab = hasNotificationAudience(
    roles,
    NotificationAudience.records,
  );
  const showWallTab = hasNotificationAudience(
    roles,
    NotificationAudience.wall,
  );

  useEffect(() => {
    if (userAudiences.length === 1) {
      setAudienceTab(userAudiences[0]);
    }
  }, [userAudiences]);

  const canMarkRead =
    isPrivilegedTransaction(roles, RoleTypes.MarkAsReadNotifications) ||
    isPrivilegedTransaction(roles, RoleTypes.ViewWall);
  const canDelete = isPrivilegedTransaction(
    roles,
    RoleTypes.DeleteNotifications,
  );

  const statusFilter =
    statusTab === "all" ? undefined : (statusTab as NotificationStatus);

  const audienceFilter =
    audienceTab === "all" ? undefined : audienceTab;

  useEffect(() => {
    const skipRows = currentPage * rowsPerPage;
    dispatch(
      fetchNotifications({
        skip: skipRows,
        take: rowsPerPage,
        status: statusFilter,
        audience: audienceFilter,
      }),
    );
  }, [
    dispatch,
    currentPage,
    rowsPerPage,
    statusTab,
    statusFilter,
    audienceTab,
    audienceFilter,
  ]);

  const notifyTotal = Math.max(
    totalCount ?? 0,
    currentPage * rowsPerPage + notifications.length,
  );

  const handleMarkAsRead = (id: string) => {
    dispatch(markAsReadNotification(id));
  };

  const handleDelete = (id: string) => {
    dispatch(removeNotification(id));
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsReadNotifications());
  };

  const handleEnablePush = async () => {
    const ok = await enablePush();
    if (ok) dismissPushPrompt();
    setPushSnackbar(true);
  };

  return (
    <div>
      <Helmet>
        <title>{`${t("notifications")} | ${t("platform")}`}</title>
      </Helmet>
      <Container maxWidth="lg">
        <Box sx={{ mt: 2, py: 3 }}>
          <Typography variant="h4" gutterBottom>
            {t("notifications")}
          </Typography>

          {pushSupported && (
            <Card sx={{ mb: 3 }}>
              <CardHeader
                title={t("pushNotifications")}
                subheader={
                  pushPermission === "denied"
                    ? t("pushDenied")
                    : pushSubscribed
                      ? t("pushEnabled")
                      : t("enablePushHint")
                }
              />
              <Divider />
              <CardContent>
                <Stack direction="row" spacing={2}>
                  {!pushSubscribed ? (
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<NotificationsActive />}
                      onClick={handleEnablePush}
                      disabled={pushPermission === "denied"}
                    >
                      {t("enablePush")}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<NotificationsOff />}
                      onClick={disablePush}
                    >
                      {t("disablePush")}
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          <Stack spacing={2} sx={{ mb: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={2}
            >
              <Tabs
                value={statusTab}
                onChange={(_, v) => {
                  setStatusTab(v);
                  setCurrentPage(0);
                }}
              >
                <Tab label={t("new")} value="new" />
                <Tab label={t("read")} value="read" />
                <Tab label={t("all")} value="all" />
              </Tabs>
              {canMarkRead &&
                statusTab === "new" &&
                notifications.length > 0 && (
                  <Button variant="outlined" onClick={handleMarkAllAsRead}>
                    {t("markAllAsRead")}
                  </Button>
                )}
            </Stack>

            {userAudiences.length > 0 && (
              <Tabs
                value={audienceTab}
                onChange={(_, v) => {
                  setAudienceTab(v);
                  setCurrentPage(0);
                }}
                variant="scrollable"
                scrollButtons="auto"
              >
                {userAudiences.length > 1 && (
                  <Tab label={t("all")} value="all" />
                )}
                {showRecordsTab && (
                  <Tab
                    label={t("notificationAudienceRecords")}
                    value={NotificationAudience.records}
                  />
                )}
                {showPipelineTab && (
                  <Tab
                    label={t("notificationAudiencePipeline")}
                    value={NotificationAudience.pipeline}
                  />
                )}
                {showWallTab && (
                  <Tab
                    label={t("notificationAudienceWall")}
                    value={NotificationAudience.wall}
                  />
                )}
              </Tabs>
            )}
          </Stack>

          <NotificationTable
            notifications={notifications as Notification[]}
            loading={loading === "pending"}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            showActions
            canMarkRead={canMarkRead && statusTab === "new"}
            canDelete={canDelete}
            onMarkAsRead={handleMarkAsRead}
            onDelete={handleDelete}
          />

          <Box sx={{ mt: 2 }}>
            <Paging
              total={notifyTotal}
              rowsPerPage={rowsPerPage}
              currentPage={currentPage}
              setRowsPerPage={setRowsPerPage}
              setCurrentPage={setCurrentPage}
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {t("total")}: {notifyTotal}
            </Typography>
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={shouldShowPushPrompt || pushSnackbar}
        message={
          shouldShowPushPrompt
            ? t("enablePushHint")
            : pushSubscribed
              ? t("pushEnabled")
              : ""
        }
        action={
          shouldShowPushPrompt ? (
            <>
              <Button color="secondary" size="small" onClick={handleEnablePush}>
                {t("enablePush")}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  dismissPushPrompt();
                  setPushSnackbar(false);
                }}
              >
                {t("dismiss")}
              </Button>
            </>
          ) : (
            <Button size="small" onClick={() => setPushSnackbar(false)}>
              {t("close")}
            </Button>
          )
        }
      />
    </div>
  );
}

export default Notifications;
